import { X } from 'lucide-react';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { TaskSideloadContent } from './TaskSideloadContent';

export interface TaskDetailOverlayProps {
  taskId: number;
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
  isReadOnly?: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

export function TaskDetailOverlay({
  taskId,
  workspaceMembers,
  isReadOnly = false,
  onClose,
  onDeleted,
}: TaskDetailOverlayProps) {
  const utils = trpc.useUtils();
  const taskQuery = trpc.tasks.getById.useQuery({ id: taskId });

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      utils.tasks.getById.invalidate({ id: taskId });
    },
  });

  const deleteMutation = trpc.tasks.delete.useMutation({
    onSuccess: () => {
      onDeleted?.();
      onClose();
    },
  });

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-xs"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px] lg:w-[420px] xl:w-[480px] bg-background border-l shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <h3 className="font-semibold text-sm">Task</h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {taskQuery.data && (
            <TaskSideloadContent
              task={taskQuery.data}
              workspaceMembers={workspaceMembers}
              isSubmitting={updateMutation.isPending}
              isDeleting={deleteMutation.isPending}
              onClose={onClose}
              onDelete={() => {
                if (
                  confirm(
                    'Are you sure you want to delete this task? This action cannot be undone.'
                  )
                ) {
                  deleteMutation.mutate({ id: taskId });
                }
              }}
              onSubmit={(data) => {
                updateMutation.mutate({ id: taskId, ...data });
              }}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
      </div>
    </>
  );
}
